import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
    ShieldCheck,
    Eye,
    EyeOff,
    AlertCircle,
    AlertTriangle,
    Lock,
    Radio,
    Scan,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import "./login-ar.css";

const LOGO_URL = "/static/img/mbs-logo.png";

const SECURITY_WARNINGS = [
    {
        id: "auth",
        level: "danger",
        icon: Lock,
        title: "Yetkisiz erişim yasaktır",
        text: "Bu panel yalnızca yetkili MBS personeli içindir. İzinsiz giriş denemeleri kayıt altına alınır.",
    },
    {
        id: "kvkk",
        level: "warn",
        icon: ShieldCheck,
        title: "KVKK · Gizlilik",
        text: "Müvekkil ve dosya verileri 6698 sayılı Kanun kapsamında korunur. Ekran görüntüsü paylaşımı yasaktır.",
    },
    {
        id: "session",
        level: "warn",
        icon: Radio,
        title: "Oturum izleme aktif",
        text: "Tüm giriş ve kritik işlemler denetim loglarına işlenir. Ortak cihazlarda çıkış yapmayı unutmayın.",
    },
    {
        id: "ar",
        level: "info",
        icon: Scan,
        title: "AR güvenlik katmanı",
        text: "Artırılmış gerçeklik arayüzü yalnızca görsel rehberlik sağlar. Kimlik doğrulama sunucu tarafında yapılır.",
    },
];

const TICKER_TEXT =
    "⚠ GÜVENLİK UYARISI — Yetkisiz erişim cezai sorumluluk doğurur · KVKK kapsamında kişisel veriler korunur · Oturumlar loglanır · Şifrenizi kimseyle paylaşmayın · AR modu aktif · ";

function ArParticles() {
    const dots = useMemo(
        () =>
            Array.from({ length: 18 }, (_, i) => ({
                id: i,
                left: `${8 + ((i * 17) % 84)}%`,
                top: `${12 + ((i * 23) % 76)}%`,
                delay: `${(i % 6) * 0.7}s`,
            })),
        [],
    );
    return (
        <>
            {dots.map((d) => (
                <span
                    key={d.id}
                    className="ar-particle"
                    style={{ left: d.left, top: d.top, animationDelay: d.delay }}
                />
            ))}
        </>
    );
}

function ArWarningStack({ shake }) {
    return (
        <div className={`space-y-3 ${shake ? "animate-[shake_0.4s_ease-in-out]" : ""}`}>
            {SECURITY_WARNINGS.map((w) => {
                const Icon = w.icon;
                const cls =
                    w.level === "danger"
                        ? "ar-warn-card ar-danger-card rounded-lg p-3"
                        : "ar-warn-card rounded-lg p-3";
                return (
                    <div key={w.id} className={cls}>
                        <div className="flex gap-2 pl-2">
                            <Icon
                                className={`h-4 w-4 shrink-0 mt-0.5 ${
                                    w.level === "danger"
                                        ? "text-red-400"
                                        : w.level === "warn"
                                          ? "text-amber-400"
                                          : "text-cyan-400"
                                }`}
                            />
                            <div>
                                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-200">
                                    {w.title}
                                </div>
                                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                                    {w.text}
                                </p>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function Login() {
    const { isAuthenticated, signIn, loading } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [otp, setOtp] = useState("");
    const [otpStep, setOtpStep] = useState(false);
    const [challengeId, setChallengeId] = useState("");
    const [emailHint, setEmailHint] = useState("");
    const [show, setShow] = useState(false);
    const [err, setErr] = useState(null);
    const [coords, setCoords] = useState({ x: "41.0082", y: "28.9784", z: "0.00" });
    const [shake, setShake] = useState(false);

    useEffect(() => {
        const t = setInterval(() => {
            setCoords({
                x: (41 + Math.random() * 0.01).toFixed(4),
                y: (28.9 + Math.random() * 0.02).toFixed(4),
                z: (Math.random() * 2).toFixed(2),
            });
        }, 2200);
        return () => clearInterval(t);
    }, []);

    if (isAuthenticated) {
        const redirect = location.state?.from?.pathname || "/";
        return <Navigate to={redirect} replace />;
    }

    async function onSubmit(e) {
        e.preventDefault();
        setErr(null);
        try {
            if (otpStep) {
                const result = await signIn(username.trim(), password, {
                    username: username.trim(),
                    password,
                    otp: otp.trim(),
                    challenge_id: challengeId,
                });
                if (result?.otpRequired) {
                    setErr("Doğrulama kodu tekrar istendi. E-postanızı kontrol edin.");
                    return;
                }
            } else {
                const result = await signIn(username.trim(), password);
                if (result?.otpRequired) {
                    setOtpStep(true);
                    setChallengeId(result.challenge_id || "");
                    setEmailHint(result.email_hint || "");
                    setOtp("");
                    return;
                }
            }
            const redirect = location.state?.from?.pathname || "/";
            navigate(redirect, { replace: true });
        } catch (e) {
            setShake(true);
            setTimeout(() => setShake(false), 500);
            const data = e?.response?.data;
            let msg;
            if (!e?.response) {
                const code = e?.code || "";
                if (code === "ERR_NETWORK" || e?.message === "Network Error") {
                    msg =
                        "Sunucuya bağlanılamadı (Network Error). " +
                        "Yerel geliştirmede Django API çalışıyor olmalı (ör. python manage.py runserver). " +
                        ".env içinde REACT_APP_API_BASE_URL=/api ve REACT_APP_DEV_PROXY_TARGET=http://127.0.0.1:8000 kullanın.";
                } else {
                    msg = e?.message || "Sunucuya bağlanılamadı.";
                }
            } else {
                msg =
                    (data &&
                        (data.detail ||
                            data.non_field_errors?.[0] ||
                            Object.values(data).flat?.()?.[0])) ||
                    e?.message ||
                    "Giriş başarısız. Bilgilerinizi kontrol edin.";
            }
            setErr(typeof msg === "string" ? msg : JSON.stringify(msg));
        }
    }

    return (
        <div className="ar-login-root min-h-screen w-full flex flex-col">
            {/* Üst uyarı şeridi */}
            <div className="ar-warning-ticker relative z-20 shrink-0">
                <div className="ar-warning-ticker-inner">
                    {TICKER_TEXT}
                    {TICKER_TEXT}
                </div>
            </div>

            <div className="flex-1 grid lg:grid-cols-2 relative ar-scene">
                <div className="ar-scanline" />
                <div className="ar-hud-corner tl" />
                <div className="ar-hud-corner tr" />
                <div className="ar-hud-corner bl" />
                <div className="ar-hud-corner br" />

                {/* Sol: AR sahne + uyarılar */}
                <div className="relative hidden lg:flex flex-col justify-between overflow-hidden border-r border-cyan-500/10 p-8">
                    <div className="ar-grid-floor" />
                    <ArParticles />
                    <div className="ar-crosshair hidden xl:block" />
                    <div
                        className="ar-reticle-ring hidden xl:block"
                        style={{
                            width: 280,
                            height: 280,
                            top: "38%",
                            left: "28%",
                            transform: "translate(-50%, -50%)",
                        }}
                    />
                    <div
                        className="ar-reticle-ring hidden xl:block"
                        style={{
                            width: 360,
                            height: 360,
                            top: "38%",
                            left: "28%",
                            transform: "translate(-50%, -50%)",
                            animationDelay: "1.2s",
                        }}
                    />

                    <div className="relative z-10 flex flex-col items-center text-center">
                        <div className="ar-logo-frame h-40 w-40 rounded-xl bg-[#0B1437]/90 flex items-center justify-center mb-6">
                            <img
                                src={LOGO_URL}
                                alt="MBS Hukuk"
                                className="h-32 w-32 object-contain"
                            />
                        </div>
                        <div className="font-display text-4xl gold-text tracking-tight">
                            MBS Hukuk
                        </div>
                        <div className="ar-label-tag mt-2">AR · SECURE PORTAL v2</div>
                        <div className="ar-coords mt-4">
                            LAT {coords.x} · LNG {coords.y} · ALT {coords.z}m
                        </div>
                    </div>

                    <div className="relative z-10 max-w-md w-full mx-auto">
                        <ArWarningStack shake={shake} />
                    </div>

                    <div className="relative z-10 ar-coords text-center opacity-60">
                        SCAN · AUTH · ENCRYPT · SESSION
                    </div>
                </div>

                {/* Sağ: Giriş formu */}
                <div className="flex items-center justify-center p-6 sm:p-10 relative">
                    <ArParticles />
                    <form
                        onSubmit={onSubmit}
                        className={`ar-form-panel w-full max-w-md rounded-2xl p-6 sm:p-8 animate-fade-in relative z-10 ${shake ? "animate-[shake_0.4s_ease-in-out]" : ""}`}
                    >
                        <div className="lg:hidden flex flex-col items-center text-center mb-6">
                            <div className="ar-logo-frame h-20 w-20 rounded-xl flex items-center justify-center mb-3">
                                <img
                                    src={LOGO_URL}
                                    alt="MBS Hukuk"
                                    className="h-16 w-16 object-contain"
                                />
                            </div>
                            <div className="font-display text-2xl gold-text">MBS Hukuk</div>
                            <div className="ar-label-tag mt-1">AR Giriş</div>
                        </div>

                        <div className="flex items-center gap-2 mb-1">
                            <Scan className="h-4 w-4 text-cyan-400" />
                            <span className="ar-label-tag">Kimlik doğrulama</span>
                        </div>
                        <h2 className="font-display text-3xl text-[#F5ECD6] tracking-tight">
                            Panele Giriş
                        </h2>
                        <p className="text-sm text-slate-400 mt-2">
                            {otpStep
                                ? `E-posta adresinize (${emailHint || "kayıtlı adres"}) 6 haneli doğrulama kodu gönderildi.`
                                : "AR güvenlik katmanı aktif. Yetkili hesabınızla devam edin."}
                        </p>

                        {/* Mobil uyarı özeti */}
                        <div className="lg:hidden mt-4 ar-warn-card rounded-lg p-3">
                            <div className="flex gap-2 text-amber-300 text-[11px]">
                                <AlertTriangle className="h-4 w-4 shrink-0" />
                                <span>
                                    Yetkisiz erişim yasaktır. Oturumlar loglanır. KVKK kapsamında
                                    veriler korunur.
                                </span>
                            </div>
                        </div>

                        {err && (
                            <div
                                data-testid="login-error"
                                className="mt-5 px-3 py-3 rounded-lg border border-red-500/50 bg-red-500/10 text-red-200 text-xs flex items-start gap-2 relative overflow-hidden"
                                role="alert"
                            >
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500 animate-pulse" />
                                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-red-400" />
                                <div>
                                    <div className="font-semibold uppercase tracking-wider text-[10px] text-red-300 mb-1">
                                        AR Güvenlik Uyarısı
                                    </div>
                                    <span className="break-words">{err}</span>
                                </div>
                            </div>
                        )}

                        <div className="mt-6 space-y-4">
                            <div>
                                <label className="ar-label-tag block mb-1.5">
                                    Kullanıcı · UID
                                </label>
                                <input
                                    data-testid="login-username-input"
                                    type="text"
                                    autoComplete="username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                    disabled={otpStep}
                                    className="ar-input w-full rounded-md px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition disabled:opacity-60"
                                    placeholder="kullanici_adi"
                                />
                            </div>
                            {!otpStep ? (
                            <div>
                                <label className="ar-label-tag block mb-1.5">
                                    Şifre · PASSKEY
                                </label>
                                <div className="relative">
                                    <input
                                        data-testid="login-password-input"
                                        type={show ? "text" : "password"}
                                        autoComplete="current-password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="ar-input w-full rounded-md px-3 py-2.5 pr-10 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition"
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShow((s) => !s)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded hover:bg-cyan-500/10 flex items-center justify-center text-slate-500 hover:text-cyan-300"
                                        tabIndex={-1}
                                        aria-label={show ? "Şifreyi gizle" : "Şifreyi göster"}
                                    >
                                        {show ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                            ) : (
                            <div>
                                <label className="ar-label-tag block mb-1.5">
                                    E-posta doğrulama kodu
                                </label>
                                <input
                                    data-testid="login-otp-input"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    required
                                    maxLength={6}
                                    className="ar-input w-full rounded-md px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition tracking-[0.35em] text-center font-mono"
                                    placeholder="000000"
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        setOtpStep(false);
                                        setOtp("");
                                        setChallengeId("");
                                        setEmailHint("");
                                    }}
                                    className="mt-2 text-[11px] text-cyan-400 hover:text-cyan-300"
                                >
                                    ← Şifre adımına dön
                                </button>
                            </div>
                            )}

                            <button
                                data-testid="login-submit-button"
                                type="submit"
                                disabled={loading}
                                className="ar-btn-glow w-full h-11 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 mt-2"
                            >
                                {loading ? (
                                    <div className="h-4 w-4 rounded-full border-2 border-black/40 border-t-transparent animate-spin" />
                                ) : (
                                    <ShieldCheck className="h-4 w-4" />
                                )}
                                {otpStep ? "Kodu Doğrula" : "AR Güvenli Giriş"}
                            </button>
                        </div>

                        <div className="mt-6 pt-4 border-t border-cyan-500/15">
                            <div className="flex items-start gap-2 text-[10px] text-slate-500 leading-relaxed">
                                <Lock className="h-3.5 w-3.5 shrink-0 mt-0.5 text-cyan-600" />
                                <span>
                                    Token tabanlı oturum · TLS şifreleme · Denetim kaydı. Bu
                                    arayüz artırılmış gerçeklik görsel katmanıdır; kamera veya
                                    konum verisi toplanmaz.
                                </span>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            <style>{`
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    20% { transform: translateX(-6px); }
                    40% { transform: translateX(6px); }
                    60% { transform: translateX(-4px); }
                    80% { transform: translateX(4px); }
                }
            `}</style>
        </div>
    );
}
